import React, { useEffect, useState } from 'react';
import { Box, Typography, Switch } from '@material-ui/core';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SnackbarOmni from 'allGlobalComponents/SnackbarOmni';
import TableOmniNewAnimated from 'allGlobalComponents/TableOmniNewAnimated';
import ButtonOmniNew from 'allGlobalComponents/ButtonOmniNew';
import ModalOmni from 'allGlobalComponents/ModalOmni';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import FormFieldOmni from 'allGlobalComponents/FormFieldOmni';
import DeleteConfirmationDialog from 'allGlobalComponents/DeleteConfirmationDoalog';
import { useSnackbar, usePagination, useApi } from 'app/hooks';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import DateFilterWithExport from 'allGlobalComponents/DateFilterWithExport';

const useStyles = makeStyles(theme => ({
    topBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: theme.spacing(2),
        margin: '0 20px',
        marginBottom: theme.spacing(2),
        flexWrap: 'wrap'
    },
    formContent: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5)
    },
    buttonContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(2)
    },
    switch: {
        '& .MuiSwitch-switchBase': {
            color: theme.palette.type === 'dark' ? '#bdbdbd' : undefined
        },
        '& .MuiSwitch-track': {
            backgroundColor: theme.palette.type === 'dark' ? '#757575' : undefined
        }
    },
}));

const INITIAL_FORM = {
    name: '',
    code: '',
    configPairs: []
};

function ChannelsTab() {
    const classes = useStyles();
    const theme = useTheme();

    // Hooks
    const { snackbar, showSuccess, showError, showWarning } = useSnackbar();
    const { currentParams, setCurrentParams, totalItems, setTotalItems } = usePagination(10);
    const { isLoading, request } = useApi();

    // State
    const [data, setData] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [formData, setFormData] = useState(INITIAL_FORM);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [nameExists, setNameExists] = useState(false);
    const [codeExists, setCodeExists] = useState(false);
    const [viewOpen, setViewOpen] = useState(false);
    const [viewData, setViewData] = useState([]);
    const [viewLoading, setViewLoading] = useState(false);
    const [togglingId, setTogglingId] = useState(null);
    const [toggleOpen, setToggleOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    // Fetch channels
    const fetchChannels = () => {
        request({
            module: 'saChannels',
            method: 'getChannels',
            payload: {},
            onSuccess: (response) => {
                const channels = response.data.data.channels || [];
                setData(channels);
                setTotalItems(channels.length);
            },
            onError: (message) => showError(message || 'Error fetching channels')
        });
    };

    useEffect(() => {
        fetchChannels();
    }, [refreshKey]);

    // Form handlers
    const handleFormChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        if (field === 'name') {
            const exists = data.some(ch =>
                ch.name?.toLowerCase() === value.trim().toLowerCase() &&
                ch.id !== selectedRow?.id
            );
            setNameExists(exists);
        }
        if (field === 'code') {
            const exists = data.some(ch =>
                ch.code?.toLowerCase() === value.trim().toLowerCase() &&
                ch.id !== selectedRow?.id
            );
            setCodeExists(exists);
        }
    };

    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };

    const resetForm = () => {
        setFormData(INITIAL_FORM);
        setNameExists(false);
        setCodeExists(false);
    };

    const handleToggleOpen = (row) => {
        setSelectedRow(row);
        setToggleOpen(true);
    };

    const handleToggleConfirm = () => {
        setTogglingId(selectedRow.id);
        setToggleOpen(false);
        request({
            module: 'saChannels',
            method: 'toggleChannel',
            payload: { id: selectedRow.id },
            onSuccess: (response) => {
                showSuccess('Channel toggled successfully');
                fetchChannels();
                setTogglingId(null);
                setSelectedRow(null);
            },
            onError: (message) => {
                showError(message || 'Error toggling channel');
                setTogglingId(null);
                setSelectedRow(null);
            },
            onFinally: () => setTogglingId(null)
        });
    };

    // Build API payload from form
    const buildPayload = () => {
        if (!formData.name || !formData.code) return null;

        let configuration = null;
        if (formData.configPairs.length > 0) {
            configuration = {};
            for (const pair of formData.configPairs) {
                if (!pair.key.trim() || !pair.value.trim()) {
                    showWarning('Please fill in all configuration key-value pairs or remove empty ones');
                    return null;
                }
                configuration[pair.key.trim()] = pair.value.trim();
            }
        }

        return {
            name: formData.name,
            code: formData.code,
            configuration
        };
    };

    // Create
    const handleCreate = () => {
        if (!formData.name || !formData.code) {
            showWarning('Please fill in channel name and code');
            return;
        }
        // rest stays the same

        const payload = buildPayload();
        if (!payload) return;

        setSaving(true);
        request({
            module: 'saChannels',
            method: 'createChannel',
            payload,
            onSuccess: () => {
                showSuccess('Channel created successfully');
                setCreateOpen(false);
                resetForm();
                fetchChannels();
                setSaving(false)
            },
            onError: (message) => {
                showError(message || 'Error creating channel')
                setSaving(false)
            },
            onFinally: () => setSaving(false)
        });
    };

    // Edit open
    const handleEditOpen = (row) => {
        setSelectedRow(row);

        // Convert configuration object to key-value pairs array
        let configPairs = [];
        if (row.configuration && typeof row.configuration === 'object') {
            configPairs = Object.entries(row.configuration).map(([key, value]) => ({
                key,
                value: String(value)
            }));
        }

        setFormData({
            name: row.name || '',
            code: row.code || '',
            configPairs
        });
        setEditOpen(true);
    };

    // Update
    const handleUpdate = () => {
        if (!formData.name || !formData.code) {
            showWarning('Please fill in channel name and code');
            return;
        }

        const payload = buildPayload();
        if (!payload) return;

        payload.id = selectedRow.id;

        setSaving(true);
        request({
            module: 'saChannels',
            method: 'updateChannel',
            payload,
            onSuccess: () => {
                showSuccess('Channel updated successfully');
                setEditOpen(false);
                resetForm();
                setSelectedRow(null);
                fetchChannels();
                setSaving(false)
            },
            onError: (message) => {
                showError(message || 'Error updating channel')
                setSaving(false)
            },
            onFinally: () => setSaving(false)
        });
    };

    // Delete open
    const handleDeleteOpen = (row) => {
        setSelectedRow(row);
        setDeleteOpen(true);
    };

    // Delete confirm
    const handleDeleteConfirm = () => {
        setDeleting(true);
        request({
            module: 'saChannels',
            method: 'deleteChannel',
            payload: { id: selectedRow.id },
            onSuccess: () => {
                showSuccess('Channel deleted successfully');
                setDeleteOpen(false);
                setSelectedRow(null);
                fetchChannels();
                setDeleting(false)
            },
            onError: (message) => {
                showError(message || 'Error deleting channel')
                setDeleting(false)
            },
            onFinally: () => setDeleting(false)
        });
    };

    // View clients by channel
    const handleViewClients = (row) => {
        setSelectedRow(row);
        setViewData([]);
        setViewOpen(true);
        setViewLoading(true);
        request({
            module: 'saChannels',
            method: 'clientChannelsClientsByChannel',
            payload: { channelId: row.id },
            onSuccess: (response) => {
                setViewData(response.data.data.data || []);
                setViewLoading(false);
            },
            onError: (message) => {
                showError(message || 'Error fetching channel clients');
                setViewData([]);
                setViewLoading(false);
            },
            onFinally: () => setViewLoading(false)
        });
    };

    const handleAddConfigPair = () => {
        setFormData(prev => ({
            ...prev,
            configPairs: [...prev.configPairs, { key: '', value: '' }]
        }));
    };

    const handleRemoveConfigPair = (index) => {
        setFormData(prev => ({
            ...prev,
            configPairs: prev.configPairs.filter((_, i) => i !== index)
        }));
    };

    const handleConfigPairChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            configPairs: prev.configPairs.map((pair, i) =>
                i === index ? { ...pair, [field]: value } : pair
            )
        }));
    };

    const isFormInvalid = () => {
        const hasEmptyConfigPairs = formData.configPairs.some(
            pair => !pair.key.trim() || !pair.value.trim()
        );
        return !formData.name.trim() || !formData.code.trim() || nameExists || codeExists || saving || hasEmptyConfigPairs;
    };
    // Table columns
    const headCells = [
        { id: 'id', numeric: false, disablePadding: false, label: 'ID' },
        { id: 'name', numeric: false, disablePadding: false, label: 'Name', tooltip: true },
        { id: 'code', numeric: false, disablePadding: false, label: 'Code', tooltip: true },
        {
            id: 'is_active',
            numeric: false,
            disablePadding: false,
            label: 'Status',
            pill: true,
            pillConfig: {
                mapping: {
                    true: {
                        label: 'Active',
                        color: '#4caf50',
                        bgColor: 'rgba(76, 175, 80, 0.1)',
                        icon: <CheckCircleIcon fontSize="small" />
                    },
                    false: {
                        label: 'Inactive',
                        color: '#f44336',
                        bgColor: 'rgba(244, 67, 54, 0.1)',
                        icon: <CancelIcon fontSize="small" />
                    }
                },
                defaultColor: '#9e9e9e',
                defaultBgColor: 'rgba(158, 158, 158, 0.1)'
            }
        },
        {
            id: 'configuration',
            numeric: false,
            disablePadding: false,
            label: 'Configuration',
            msg: true,
            truncateLength: 30,
            customRow: row => row.configuration ? JSON.stringify(row.configuration) : '-'
        },
        { id: 'dt', numeric: false, disablePadding: false, label: 'Created', dt: true },
        { id: 'dtu', numeric: false, disablePadding: false, label: 'Updated', dt: true },
        {
            id: 'view_clients',
            numeric: false,
            disablePadding: false,
            label: 'Clients',
            customRow: row => (
                <Typography
                    variant="body2"
                    style={{
                        color: theme.palette.type === 'dark' ? '#9CA3AF' : '#6B7280',
                        fontWeight: 500,
                        cursor: 'pointer',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleViewClients(row);
                    }}
                >
                    View Clients
                </Typography>
            )
        },
        {
            id: 'toggle',
            numeric: false,
            disablePadding: false,
            label: 'Active',
            customRow: row => (
                <Switch
                    checked={row.is_active === true}
                    onChange={() => handleToggleOpen(row)}
                    disabled={togglingId === row.id}
                    className={classes.switch}
                    size="small"
                />
            )
        },
        { id: 'edit', numeric: false, disablePadding: false, label: 'Edit' },
        { id: 'delete', numeric: false, disablePadding: false, label: 'Delete' }
    ];

    // Form content for create/edit dialog
    const renderFormContent = () => (
        <div className={classes.formContent}>
            <FormFieldOmni
                label="Channel Name"
                variant="outlined"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                type="text"
                size="small"
                fullWidth
                placeholder="e.g. WhatsApp"
            />
            {nameExists && (
                <Typography variant="caption" style={{ color: '#f44336', marginTop: -4 }}>
                    Channel name already exists
                </Typography>
            )}
            <FormFieldOmni
                label="Channel Code"
                variant="outlined"
                value={formData.code}
                onChange={(e) => handleFormChange('code', e.target.value)}
                type="text"
                size="small"
                fullWidth
                placeholder="e.g. whatsapp"
            />
            {codeExists && (
                <Typography variant="caption" style={{ color: '#f44336', marginTop: -4 }}>
                    Channel code already exists
                </Typography>
            )}
            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginTop: 8 }}>
                <Typography
                    variant="subtitle1"
                    style={{ fontWeight: 600 }}
                    color="textPrimary"
                >
                    Configuration
                </Typography>
                <ButtonOmni
                    onClick={handleAddConfigPair}
                    variant="outlined"
                    label="+ Add"
                    size="small"
                />
            </Box>
            {formData.configPairs.map((pair, index) => (
                <Box key={index} display="flex" alignItems="center" gap={1} style={{ gap: 8 }}>
                    <FormFieldOmni
                        label="Key"
                        variant="outlined"
                        value={pair.key}
                        onChange={(e) => handleConfigPairChange(index, 'key', e.target.value)}
                        type="text"
                        size="small"
                        fullWidth
                        placeholder="e.g. api_url"
                    />
                    <FormFieldOmni
                        label="Value"
                        variant="outlined"
                        value={pair.value}
                        onChange={(e) => handleConfigPairChange(index, 'value', e.target.value)}
                        type="text"
                        size="small"
                        fullWidth
                        placeholder="e.g. https://example.com"
                    />
                    <DeleteIcon
                        fontSize="large"
                        style={{ cursor: 'pointer', color: theme.palette.secondary.value, flexShrink: 0 }}
                        onClick={() => handleRemoveConfigPair(index)}
                    />
                </Box>
            ))}
        </div>
    );

    // Create dialog actions
    const createActions = (
        <div className={classes.buttonContainer}>
            <ButtonOmni onClick={() => { setCreateOpen(false); resetForm(); }} variant="outlined" label="Cancel" disabled={saving} />
            <ButtonOmni onClick={handleCreate} variant="outlined" label={saving ? 'Creating...' : 'Create'} disabled={isFormInvalid()} />
        </div>
    );

    // Edit dialog actions
    const editActions = (
        <div className={classes.buttonContainer}>
            <ButtonOmni onClick={() => { setEditOpen(false); resetForm(); setSelectedRow(null); }} variant="outlined" label="Cancel" disabled={saving} />
            <ButtonOmni onClick={handleUpdate} variant="outlined" label={saving ? 'Updating...' : 'Update'} disabled={isFormInvalid()} />
        </div>
    );

    return (
        <div className="w-full mx-auto">
            {/* Top bar with Create & Export */}
            <DateFilterWithExport
                showDateRange={false}
                showExport={false}
                filterFields={[]}
                filterValues={{}}
                onFiltersApply={() => { }}
                onFiltersClear={() => { }}
                showRefresh={true}
                onRefresh={handleRefresh}
                customRightContent={
                    <ButtonOmniNew
                        buttonType="create"
                        label="Add Channel"
                        onClick={() => { resetForm(); setCreateOpen(true); }}
                        variant="outlined"
                    />
                }
            />

            {/* Table */}
            <TableOmniNewAnimated
                rows={data}
                headCells={headCells}
                perPageRows={currentParams.limit}
                setCurrentParams={setCurrentParams}
                totalItems={totalItems}
                curPage={currentParams.page}
                loading={isLoading}
                onEditClick={handleEditOpen}
                onDeleteClick={handleDeleteOpen}
            />

            {/* Create Dialog */}
            <ModalOmni
                disableBackdropClick
                open={createOpen}
                handleClose={() => { setCreateOpen(false); resetForm(); }}
                title="Add Channel"
                content={renderFormContent()}
                actions={createActions}
                customWidth={500}
            />

            {/* Edit Dialog */}
            <ModalOmni
                disableBackdropClick
                open={editOpen}
                handleClose={() => { setEditOpen(false); resetForm(); setSelectedRow(null); }}
                title="Edit Channel"
                content={renderFormContent()}
                actions={editActions}
                customWidth={500}
            />

            {/* Delete Confirmation */}
            <DeleteConfirmationDialog
                open={deleteOpen}
                onClose={() => { setDeleteOpen(false); setSelectedRow(null); }}
                onConfirm={handleDeleteConfirm}
                title="Delete Channel"
                message={`Are you sure you want to delete the channel "${selectedRow?.name || ''}"? This action cannot be undone.`}
                loading={deleting}
            />

            {/* View Channel Clients Dialog */}
            <ModalOmni
                disableBackdropClick
                open={viewOpen}
                handleClose={() => { setViewOpen(false); setViewData([]); setSelectedRow(null); }}
                title={`Clients - ${selectedRow?.name || ''}`}
                content={
                    <TableOmniNewAnimated
                        rows={viewData}
                        headCells={[
                            { id: 'id', numeric: false, disablePadding: false, label: 'ID' },
                            {
                                id: 'client_name',
                                numeric: false,
                                disablePadding: false,
                                label: 'Client',
                                msg: true,
                                customRow: row => `${row.client_first_name || ''} ${row.client_last_name || ''}`.trim() || '-',
                            },
                            { id: 'channel_app_name', numeric: false, disablePadding: false, label: 'Channel App', tooltip: true },
                            {
                                id: 'is_enabled',
                                numeric: false,
                                disablePadding: false,
                                label: 'Status',
                                pill: true,
                                pillConfig: {
                                    mapping: {
                                        true: {
                                            label: 'Enabled',
                                            color: '#4caf50',
                                            bgColor: 'rgba(76, 175, 80, 0.1)',
                                            icon: <CheckCircleIcon fontSize="small" />
                                        },
                                        false: {
                                            label: 'Disabled',
                                            color: '#f44336',
                                            bgColor: 'rgba(244, 67, 54, 0.1)',
                                            icon: <CancelIcon fontSize="small" />
                                        }
                                    },
                                    defaultColor: '#9e9e9e',
                                    defaultBgColor: 'rgba(158, 158, 158, 0.1)'
                                }
                            }
                        ]}
                        perPageRows={5}
                        totalItems={viewData.length}
                        curPage={0}
                        loading={viewLoading}
                        emptyMessage="No clients found for this channel"
                        fixedHeight={true}
                        maxTableHeight="400px"
                    />
                }
                customWidth={700}
            />

            {/* Toggle Confirmation Dialog */}
            <ModalOmni
                disableBackdropClick
                open={toggleOpen}
                handleClose={() => { setToggleOpen(false); setSelectedRow(null); }}
                title={selectedRow?.is_active ? 'Deactivate Channel' : 'Activate Channel'}
                content={
                    <div style={{ padding: 24, textAlign: 'left' }}>
                        <Typography variant="body1" color="textPrimary">
                            Are you sure you want to {selectedRow?.is_active ? 'deactivate' : 'activate'} the channel "<strong>{selectedRow?.name || ''}</strong>"?
                        </Typography>
                    </div>
                }
                actions={
                    <div className={classes.buttonContainer}>
                        <ButtonOmni
                            onClick={() => { setToggleOpen(false); setSelectedRow(null); }}
                            variant="outlined"
                            label="Cancel"
                        />
                        <ButtonOmni
                            onClick={handleToggleConfirm}
                            variant="outlined"
                            label={selectedRow?.is_active ? 'Deactivate' : 'Activate'}
                        />
                    </div>
                }
                customWidth={450}
            />

            <SnackbarOmni {...snackbar} />
        </div>
    );
}

export default React.memo(ChannelsTab);